import * as React from "react";

/**
 * Navigation icons from the design (UI_REDESIGN_GUIDE.md / mockups): a 20px grid, 1.6 stroke,
 * rounded ends. Home, Patients, Appointments, Prescriptions, Billing, IPD and Reports are drawn
 * as in the mockup; Peds calculator, Presets, Settings and Platform admin follow the same rules.
 * Sized by className (w-5 h-5), coloured by currentColor.
 */
export type NavIconProps = React.SVGProps<SVGSVGElement> & { strokeWidth?: number | string };
export type NavIcon = React.ComponentType<NavIconProps>;

function make(name: string, body: React.ReactNode) {
  const Icon = React.forwardRef<SVGSVGElement, NavIconProps>(({ strokeWidth = 1.6, ...props }, ref) => (
    <svg
      ref={ref}
      viewBox="0 0 20 20"
      width={20}
      height={20}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable={false}
      {...props}
    >
      {body}
    </svg>
  ));
  Icon.displayName = name;
  return Icon;
}

export const HomeNavIcon = make("HomeNavIcon",
  <path d="M3 9.5 10 3.5l7 6V16a1 1 0 0 1-1 1h-3.5v-4.5h-5V17H4a1 1 0 0 1-1-1z" />);

export const PatientsNavIcon = make("PatientsNavIcon", <>
  <circle cx="8" cy="7" r="3" />
  <path d="M2.5 16.5c.8-2.8 3-4.3 5.5-4.3s4.7 1.5 5.5 4.3" />
  <path d="M13.5 4.2a3 3 0 0 1 0 5.6M15.5 12.6c1 .7 1.7 2 2 3.9" />
</>);

export const AppointmentsNavIcon = make("AppointmentsNavIcon", <>
  <rect x="3" y="4.5" width="14" height="12.5" rx="1.5" />
  <path d="M3 8.5h14M7 2.8v3.4M13 2.8v3.4" />
</>);

export const PrescriptionsNavIcon = make("PrescriptionsNavIcon", <>
  <path d="M5 2.5h7l3.5 3.5v11a.5.5 0 0 1-.5.5H5a.5.5 0 0 1-.5-.5v-14A.5.5 0 0 1 5 2.5z" />
  <path d="M7.5 9.5h5M7.5 12.5h5" />
</>);

export const BillingNavIcon = make("BillingNavIcon", <>
  <rect x="2.5" y="5" width="15" height="10.5" rx="1.5" />
  <path d="M2.5 8.5h15M5.5 12.5h3" />
</>);

export const IpdNavIcon = make("IpdNavIcon", <>
  <path d="M2.5 5v11M17.5 11v5M2.5 13.5h15M2.5 11h15" />
  <circle cx="6" cy="8.3" r="1.6" />
  <path d="M9 11V8.5a1 1 0 0 1 1-1h5a2.5 2.5 0 0 1 2.5 2.5v1" />
</>);

export const ReportsNavIcon = make("ReportsNavIcon",
  <path d="M3 17h14M6 14V9M10 14V5M14 14v-3" />);

// ── Same rules, not in the mockup ────────────────────────────────────────────

/** Calculator: body, display, two rows of keys */
export const CalculatorNavIcon = make("CalculatorNavIcon", <>
  <rect x="4" y="2.5" width="12" height="15" rx="1.5" />
  <path d="M7 6h6" />
  <path d="M7.3 10h.01M10 10h.01M12.7 10h.01M7.3 13.5h.01M10 13.5h.01M12.7 13.5h.01" strokeWidth={2.2} />
</>);

/** Presets: a clipboard with a list — saved, reusable content */
export const TemplatesNavIcon = make("TemplatesNavIcon", <>
  <path d="M7.5 4H5.5a1 1 0 0 0-1 1v11.5a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1h-2" />
  <rect x="7.5" y="2.5" width="5" height="3" rx=".75" />
  <path d="M7.5 9.5h5M7.5 12.5h3" />
</>);

/** Settings: two sliders — plainer than a gear at 20px */
export const SettingsNavIcon = make("SettingsNavIcon", <>
  <path d="M3 6.5h8M15 6.5h2M3 13.5h2M9 13.5h8" />
  <circle cx="13" cy="6.5" r="2" />
  <circle cx="7" cy="13.5" r="2" />
</>);

/** Platform admin: a shield with a tick */
export const AdminNavIcon = make("AdminNavIcon", <>
  <path d="M10 2.5 16 5v4.5c0 3.7-2.5 6.6-6 8-3.5-1.4-6-4.3-6-8V5z" />
  <path d="m7.6 10 1.7 1.7 3.1-3.4" />
</>);
